import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useTimeout } from "@sonder/ui/source/hooks/useTimeout";
import { Checkmark, Gear, X } from "@sonder/ui/source/icons";
import { ToastMessage } from "@sonder/ui/source/styled/components/ToastMessage";
import { CircleButton } from "@sonder/ui/source/styled/fragments/CircleButton";
import {
  ErrorList,
  ErrorListItem,
} from "@sonder/ui/source/styled/fragments/Error";
import { Toast } from "@sonder/ui/source/styled/fragments/Toast";
import { colors } from "@sonder/ui/source/styled/theme";
import { useSegment } from "analytics/segment";
import { THIRTY_SECONDS } from "core/constants";
import { useUpdateTask } from "core/queries/UpdateTask";
import {
  SequentialJobActionPayloadWithId,
  removeSequentialJob,
} from "schedule/actions";
import { useStore } from "schedule/store";
import { refetchQueries } from "tasks/actions";
import { useStore as useTasksStore } from "tasks/store";

interface SequentialToastProps {
  job: SequentialJobActionPayloadWithId;
}

interface UseUpdateTasksSequentiallyPayload {
  job: SequentialJobActionPayloadWithId;
  onComplete?: () => void;
  onError?: (err: any) => void;
  onUpdate?: (resp: any) => void;
}
interface StyledToastProps {
  hasErrors: boolean;
}

const StyledToast = styled(Toast)<StyledToastProps>`
  background: ${props => (props.hasErrors ? colors.redD : colors.blue2)};
`;

export const useUpdateTasksSequentially = ({
  job,
  onComplete,
  onError,
  onUpdate,
}: UseUpdateTasksSequentiallyPayload) => {
  const [onUpdateTask] = useUpdateTask({});
  const total = job?.tasks?.length;
  let tempRemaining = total;
  const [isCompleted, setIsCompleted] = useState(false);
  const [remaining, setRemaining] = useState(total);
  const tempErrors: any[] = [];
  const [errors, setErrors] = useState<any[]>([]);

  const onUpdateTasksSequentially = async () => {
    const sequentialJobs = job.tasks.map(task => ({
      promise: () =>
        onUpdateTask({ variables: { taskId: task.id, ...job.variables } }),
      task,
    }));

    await sequentialJobs.reduce((prev, work) => {
      return prev
        .then(work.promise)
        .catch(err => {
          if (onError) {
            onError(err);
          }
          tempErrors.push({
            ...err,
            task: work.task,
          });
          setErrors(tempErrors);
        })
        .then(resp => {
          if (onUpdate) {
            onUpdate(resp);
          }
          tempRemaining -= 1;
          setRemaining(tempRemaining);
        });
    }, Promise.resolve());
    if (onComplete) {
      onComplete();
    }
    setIsCompleted(true);
  };

  return {
    errors,
    isCompleted,
    remaining,
    total,
    onUpdateTasksSequentially,
  };
};

export const SequentialToast = ({ job }: SequentialToastProps) => {
  const { dispatch } = useStore();
  const { dispatch: tasksStoreDispatch } = useTasksStore();
  const { track } = useSegment();
  const { errors, isCompleted, onUpdateTasksSequentially, remaining, total } =
    useUpdateTasksSequentially({
      job,
    });
  const hasErrors = errors?.length > 0;

  // Start Processing the Mutations on Mount
  useEffect(() => {
    onUpdateTasksSequentially();
  }, []);

  // Wait 30 seconds before hiding the sequential toast if complete and no errors.
  const { start } = useTimeout(
    () => job.jobId && dispatch(removeSequentialJob(job.jobId)),
    THIRTY_SECONDS,
  );
  useEffect(() => {
    if (isCompleted && !hasErrors) {
      tasksStoreDispatch(
        refetchQueries(["GetTaskSchedules", "GetSchedulerMetrics"]),
      );
      start();
    }
  }, [isCompleted, hasErrors]);

  return (
    <StyledToast hasErrors={hasErrors}>
      {isCompleted ? <Checkmark /> : <Gear />}
      <ToastMessage errors={errors} remaining={remaining} total={total}>
        <ErrorList>
          {errors.map(error => {
            const { id, internalRef } = error.task;
            return (
              <ErrorListItem
                key={id}
              >{`${internalRef} was not able to be updated.`}</ErrorListItem>
            );
          })}
        </ErrorList>
      </ToastMessage>
      <CircleButton
        backgroundVariant="dark"
        onClick={() => {
          dispatch(removeSequentialJob(job.jobId));
          track({
            eventName: "sequential_toast",
            element: "dismiss_button",
            action: "click",
            props: {},
          });
        }}
      >
        <X />
      </CircleButton>
    </StyledToast>
  );
};
